import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { EmailTemplateResult } from 'api/client-domain/email-template-service/email-template-service.types';
import { useGlobalDrawer } from 'components/drawers/global-drawer/global-drawer.hooks';
import { useHasPermission } from 'hooks/authorization.hooks';
import MenuPopover from './menu-popover/menu-popover.component';

type SearchDataCardProps = Partial<EmailTemplateResult> & {
  isLoading?: boolean;
};

const SearchDataCard = ({
  templateId,
  jsonTemplate,
  htmlTemplate,
  name,
  emailTemplateType,
  reusable,
  isLoading,
  ...rest
}: SearchDataCardProps) => {
  const {
    drawerProps: { open: openEmailEditorDrawer },
  } = useGlobalDrawer('globalEditEmailEditorDrawer');

  const hasEditPermission = useHasPermission('CREATE_EDIT_EMAIL_TEMPLATES');

  return (
    <Card
      key={templateId}
      sx={{ minWidth: 300, position: 'relative' }}
      data-testid={`SearchDataCardGridCard.${emailTemplateType}`}
    >
      <CardActionArea
        onClick={
          hasEditPermission
            ? () => {
                if (isLoading) return;

                openEmailEditorDrawer({
                  template: {
                    templateId,
                    jsonTemplate,
                    name,
                    emailTemplateType,
                    reusable,
                    ...rest,
                  },
                });
              }
            : null
        }
        data-testid={`SearchDataCardGridCard.${emailTemplateType}.Button`}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            aspectRatio: '16/9',
          }}
        >
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              sx={{
                width: '100%',
                height: '100%',
              }}
            />
          ) : (
            <Box>
              <iframe
                title="Embedded Content"
                srcDoc={htmlTemplate}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: 'scale(1)',
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
                scrolling="no"
              />
            </Box>
          )}
        </Box>
      </CardActionArea>
      <CardActions>
        <Stack direction="row" alignItems="center" flex={1} overflow="hidden">
          {isLoading ? (
            <Skeleton sx={{ flex: 1 }} />
          ) : (
            <Box sx={{ flex: 1, minWidth: 0, p: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {name}
              </Typography>
            </Box>
          )}
          {!isLoading && (
            <MenuPopover
              jsonTemplate={jsonTemplate}
              htmlTemplate={htmlTemplate}
              templateId={templateId}
              name={name}
              emailTemplateType={emailTemplateType}
              reusable={reusable}
              openEmailEditorDrawer={openEmailEditorDrawer}
              {...rest}
            />
          )}
        </Stack>
      </CardActions>
    </Card>
  );
};

export default SearchDataCard;
